import { Warranty } from '@api/types';
import { useLazyGetWarrantyClaimListQuery } from '@api/warranty.api';
import { useAppDispatch, useAppSelector, useWarrantyClaimsCount } from '@hooks';
import { store } from '@store/store';
import { getTableBlockData, initBlock, setFilters, setOffset } from '@store/table/table.slice';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';

const ITEMS_PER_PAGE = 50;

type WarrantyListContextProps = {
  children: React.ReactNode;
  id: string;
  numberItemsOnPage?: number;
};

export type WarrantyFilters = {
  statuses?: string;
  dealerId?: string;
  reportNo?: string;
  productType?: string;
  productModel?: string;
  engineNumber?: string;
  salesDateStart?: string;
  salesDateEnd?: string;
  repairDateStart?: string;
  repairDateEnd?: string;
  serialNo?: string;
  brand?: string;
  id?: string;
  claimType?: string;
  customerNo?: string;
  partNo?: string;
};

export type WarrantyListUpdateFiltersProps = {
  filterName: keyof WarrantyFilters;
  value: WarrantyFilters[keyof WarrantyFilters];
};

type WarrantyListContextType = {
  items: Warranty[];
  isWorking: boolean;
  isError: boolean;
  id: string;
  offset: number;
  totalCount: number;
  itemsPerPage: number;
  filters: WarrantyFilters;
  updateFilters: ({ filterName, value }: WarrantyListUpdateFiltersProps) => void;
  clearFilters: () => void;
  warrantyClaims: Warranty[];
};

export const WarrantyListContext = createContext<WarrantyListContextType | null>(null);

export const WarrantyListProvider = ({
  children,
  id,
  numberItemsOnPage = ITEMS_PER_PAGE,
}: WarrantyListContextProps) => {
  const { offset, filters } = useAppSelector(getTableBlockData(id)) as {
    offset: number;
    filters: WarrantyFilters;
  };
  const dispatch = useAppDispatch();

  const filtersWithValues = (filters: WarrantyFilters) =>
    Object.entries(filters).reduce((acc: Partial<WarrantyFilters>, [key, val]) => {
      if (val) {
        acc[key as keyof WarrantyFilters] = val;
      }
      return acc;
    }, {} as Partial<WarrantyFilters>);

  const [trigger, { isFetching, isLoading, isError, currentData }] =
    useLazyGetWarrantyClaimListQuery();

  const totalCount = currentData?.data?.totalCount || 0;
  const isWorking = isLoading || isFetching;
  const items = useMemo(() => currentData?.data?.items || [], [currentData?.data?.items]);
  const warrantyClaims = useMemo(() => currentData?.data?.items || [], [currentData?.data?.items]);
  const debouncedTrigger = useMemo(() => debounce(trigger, 500), [trigger]);

  const updateFilters = useCallback(
    ({ filterName, value }: WarrantyListUpdateFiltersProps) => {
      const { filters: currentFilters } = getTableBlockData(id)(store.getState()) as {
        filters: WarrantyFilters;
      };
      const newFilters = { ...(currentFilters ?? {}) };
      if (value !== '' && !Array.isArray(value)) {
        newFilters[filterName] = value;
      } else if (value.length > 0) {
        newFilters[filterName] = value;
      } else {
        delete newFilters[filterName];
      }
      dispatch(
        setFilters({
          id,
          filters: newFilters,
        })
      );
      const requestBody = {
        paging: {
          skip: offset || 0,
          take: numberItemsOnPage,
        },
        ...filtersWithValues(newFilters ?? {}),
      };
      debouncedTrigger(requestBody);
    },
    [dispatch, id, numberItemsOnPage, offset, debouncedTrigger]
  );

  useEffect(() => {
    dispatch(initBlock({ id, filters: isEmpty(filters) ? {} : filters, visibleFilters: [] }));
    trigger({
      paging: {
        skip: offset || 0,
        take: numberItemsOnPage,
      },
      ...filters,
    });
  }, [offset]);

  const clearFilters = useCallback(() => {
    dispatch(setFilters({ id, filters: {} }));
    dispatch(setOffset({ id, offset: 0 }));
    trigger({
      paging: {
        skip: offset || 0,
        take: numberItemsOnPage,
      },
    });
  }, [dispatch, id, numberItemsOnPage, offset, trigger]);
  useWarrantyClaimsCount();
  const contextValue = useMemo(
    () => ({
      items,
      isWorking,
      isError,
      id,
      offset,
      totalCount,
      itemsPerPage: numberItemsOnPage,
      filters,
      updateFilters,
      clearFilters,
      warrantyClaims,
    }),
    [
      items,
      isWorking,
      isError,
      id,
      offset,
      totalCount,
      numberItemsOnPage,
      filters,
      updateFilters,
      clearFilters,
      warrantyClaims,
    ]
  );
  return (
    <WarrantyListContext.Provider value={contextValue}>{children}</WarrantyListContext.Provider>
  );
};

export const useWarrantyListContext = () => {
  const context = useContext(WarrantyListContext);
  if (!context) {
    throw new Error('useWarrantyListContext must be used within a WarrantyListProvider');
  }
  return context;
};
